import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, Square, Volume2, Check, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { speakText } from './speakText';

const MAX_MS = 4000;

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  return ['audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => MediaRecorder.isTypeSupported?.(m)) || '';
};

export const SayItStep = ({ words, onDone }) => {
  const { api } = useAuth();
  const [idx, setIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [tries, setTries] = useState(0);
  const [passed, setPassed] = useState(0);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const word = words[idx];

  useEffect(() => {
    setResult(null); setTries(0);
    if (word) speakText(`Say: ${word}`);
  }, [word]);

  useEffect(() => () => { clearTimeout(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const check = async (blob, type) => {
    setChecking(true);
    try {
      const fd = new FormData();
      fd.append('target', word);
      fd.append('audio', blob, `say.${type.includes('mp4') ? 'mp4' : 'webm'}`);
      const r = await api.post('/lessons/pronounce', fd);
      setResult(r.data);
      setTries((t) => t + 1);
      if (r.data.correct) { setPassed((p) => p + 1); speakText('Great job!'); }
      else speakText(`Let's try again. Say: ${word}`);
    } catch {
      setResult({ error: true });
    } finally { setChecking(false); }
  };

  const start = async () => {
    const mime = pickMime();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) { setResult({ unsupported: true }); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || mime || 'audio/webm';
        check(new Blob(chunks, { type }), type);
      };
      rec.stream = stream;
      recRef.current = rec;
      rec.start();
      setRecording(true); setResult(null);
      timerRef.current = setTimeout(stop, MAX_MS);
    } catch { setResult({ denied: true }); }
  };

  const stop = () => {
    clearTimeout(timerRef.current);
    if (recRef.current?.state === 'recording') recRef.current.stop();
    setRecording(false);
  };

  const next = () => (idx + 1 >= words.length ? onDone(passed) : setIdx((i) => i + 1));
  const canSkip = tries >= 3 || result?.unsupported || result?.denied;

  if (!word) return null;
  return (
    <div className="rounded-3xl border-4 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-teal-50 p-6 sm:p-8 text-center" data-testid="say-it-step">
      <p className="text-sm font-semibold text-amber-700 mb-2">Say it out loud · {idx + 1} / {words.length}</p>
      <motion.p key={word} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="font-fredoka text-5xl sm:text-6xl font-bold text-slate-900 mb-6" data-testid="say-it-word">{word}</motion.p>
      <div className="flex items-center justify-center gap-3 mb-5">
        <Button variant="outline" size="lg" className="rounded-full h-14" onClick={() => speakText(word)} data-testid="say-it-hear-btn">
          <Volume2 className="w-5 h-5 mr-2" /> Hear it
        </Button>
        {!result?.correct && (
          <motion.div whileTap={{ scale: 0.94 }} animate={recording ? { scale: [1, 1.08, 1] } : {}} transition={recording ? { repeat: Infinity, duration: 1 } : {}}>
            <Button size="lg" disabled={checking} onClick={recording ? stop : start}
              className={`rounded-full h-16 px-8 text-lg ${recording ? 'bg-red-500 hover:bg-red-600' : 'bg-teal-600 hover:bg-teal-700'}`}
              data-testid="say-it-record-btn">
              {checking ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : recording ? <Square className="w-5 h-5 mr-2" /> : <Mic className="w-5 h-5 mr-2" />}
              {checking ? 'Listening…' : recording ? 'Stop' : 'Tap & say it'}
            </Button>
          </motion.div>
        )}
      </div>
      <div className="min-h-[56px]" data-testid="say-it-feedback">
        {result?.correct && <p className="text-teal-700 font-bold text-lg flex items-center justify-center gap-2"><Check className="w-5 h-5" /> You said it!</p>}
        {result && !result.correct && result.heard !== undefined && (
          <p className="text-slate-600">I heard “{result.heard || '…'}”. Try again!</p>
        )}
        {result?.error && <p className="text-slate-600">Zazu couldn't hear that. Try again!</p>}
        {(result?.unsupported || result?.denied) && <p className="text-slate-600">The microphone isn't available — ask a grown-up to allow it.</p>}
      </div>
      {(result?.correct || canSkip) && (
        <Button onClick={next} className="mt-2 rounded-full bg-teal-600 hover:bg-teal-700" data-testid="say-it-next-btn">
          {result?.correct ? 'Next' : 'Move on'} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
};

export default SayItStep;
